/* eslint-disable react-hooks/exhaustive-deps */
/**
 * RideContext — global state + actions for the (new) simplified Ride Tracking.
 *
 * Responsible for everything the map-level UI needs to drive a ride from
 * Start → Track → Capture → End → Review:
 *
 *   state:   rideActive, ridePaused, rideRoute, rideDistance, rideId,
 *            rideMediaAddedCount, mediaJustAdded, activeStop,
 *            isVoiceRecording, voiceRecordingSeconds,
 *            completedRideId  (when set → RideReview opens)
 *
 *   actions: startRide, pauseRide, resumeRide, endRide,
 *            uploadRideMedia(eventOrFile),
 *            startManualStop(), resumeFromStop(),
 *            startVoiceRecording(), stopVoiceRecording(),
 *            closeReview()   — dismiss the review screen
 *
 * End Ride opens the new `RideReview` screen — it does NOT open the old
 * ShareRideDialog (that UI was wiped per user spec).
 */
import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import rideTracker from '../utils/rideTracker';
import { useLanguage } from './LanguageContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RideContext = createContext(null);

export const useRide = () => useContext(RideContext);

export const RideProvider = ({ children, user }) => {
  const { t } = useLanguage();
  // ------- Core ride state -------
  const [rideActive, setRideActive] = useState(false);
  const [ridePaused, setRidePaused] = useState(false);
  const [rideId, setRideId] = useState(null);
  const [rideRoute, setRideRoute] = useState([]);
  const [rideDistance, setRideDistance] = useState(0);
  const [rideStartTime, setRideStartTime] = useState(null);
  const [pendingStartIntro, setPendingStartIntro] = useState(false);

  // ------- Media capture feedback -------
  const [rideMediaAddedCount, setRideMediaAddedCount] = useState(0);
  const [mediaJustAdded, setMediaJustAdded] = useState(false);
  // Live list of media captured during the active ride — used to render
  // thumbnails on the map AND in the controls panel so the user sees
  // confirmation beyond a counter.
  const [rideMediaList, setRideMediaList] = useState([]);

  // ------- Manual stops -------
  const [activeStop, setActiveStop] = useState(null);

  // ------- Voice recording (MediaRecorder) -------
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);
  const [voiceRecordingSeconds, setVoiceRecordingSeconds] = useState(0);
  const voiceRecorderRef = useRef(null);
  const voiceChunksRef = useRef([]);
  const voiceTimerRef = useRef(null);
  const voiceStartRef = useRef(0);

  // ------- Review screen after End -------
  const [completedRideId, setCompletedRideId] = useState(null);

  // ------- Internal refs -------
  const pendingPointsRef = useRef([]);
  const batchTimerRef = useRef(null);
  const wakeLockRef = useRef(null);

  // Check for active ride on mount
  useEffect(() => {
    if (!user) return;
    axios.get(`${API_URL}/rides/active`)
      .then(res => {
        if (res.data.active && res.data.ride) {
          setRideActive(true);
          setRideId(res.data.ride.id);
          setRideRoute(res.data.ride.points || []);
          setRideDistance(res.data.ride.distance_km || 0);
          setRideStartTime(res.data.ride.started_at);
          // P0 Item 1: Restore media list + counter so thumbnails survive
          // app restart, screen lock, backgrounding, other apps. Previously
          // the counter reset to 0 and the strip was empty after any reload.
          const media = res.data.ride.ride_media || [];
          setRideMediaList(media);
          setRideMediaAddedCount(media.length);
        }
      })
      .catch(() => {});
  }, [user]);

  // Restore from IndexedDB
  useEffect(() => {
    const savedState = rideTracker.loadState();
    if (savedState && savedState.active && savedState.rideId) {
      setRideActive(true);
      setRidePaused(savedState.paused || false);
      setRideId(savedState.rideId);
      setRideStartTime(savedState.startTime);
      setRideDistance(savedState.distance || 0);
      rideTracker.loadPoints().then(points => {
        if (points.length > 0) {
          setRideRoute(points.map(p => ({ latitude: p.latitude, longitude: p.longitude, timestamp: p.timestamp })));
        }
      });
    }
  }, []);

  // Wake Lock — keep screen on during a ride
  useEffect(() => {
    if (!rideActive) {
      if (wakeLockRef.current) { wakeLockRef.current.release().catch(() => {}); wakeLockRef.current = null; }
      return;
    }
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          wakeLockRef.current.addEventListener('release', () => { wakeLockRef.current = null; });
        }
      } catch (_e) {}
    };
    requestWakeLock();
    const handleVis = () => {
      if (document.visibilityState === 'visible' && rideActive && !wakeLockRef.current) requestWakeLock();
    };
    document.addEventListener('visibilitychange', handleVis);
    return () => {
      document.removeEventListener('visibilitychange', handleVis);
      if (wakeLockRef.current) { wakeLockRef.current.release().catch(() => {}); wakeLockRef.current = null; }
    };
  }, [rideActive]);

  // GPS tracking + batched upload to server every 10s
  useEffect(() => {
    if (!rideActive || ridePaused || !user) return;
    if (!navigator.geolocation) return;

    rideTracker.startTracking((point) => {
      setRideRoute(prev => [...prev, point]);
      pendingPointsRef.current.push(point);
    }, 5);

    const handleVis = async () => {
      if (document.visibilityState === 'visible' && rideActive) {
        try {
          const allPoints = await rideTracker.loadPoints();
          if (allPoints.length > 0) {
            // P0 Feb 2026 — SPEED FIX: previously we stripped to lat/lon/ts,
            // which zeroed speed/accuracy/heading in state every time the user
            // returned from Spotify, screen lock, or a notification. Preserve
            // the full point object so the live speedometer + replay data
            // never lose telemetry across visibility changes.
            setRideRoute(allPoints.map(p => ({
              latitude: p.latitude,
              longitude: p.longitude,
              timestamp: p.timestamp,
              speed: p.speed,
              accuracy: p.accuracy,
              heading: p.heading,
            })));
          }
        } catch (_e) {}
      }
    };
    document.addEventListener('visibilitychange', handleVis);

    batchTimerRef.current = setInterval(async () => {
      if (pendingPointsRef.current.length === 0) return;
      const batch = [...pendingPointsRef.current];
      pendingPointsRef.current = [];
      try {
        const res = await axios.post(`${API_URL}/rides/update`, { points: batch });
        setRideDistance(res.data.distance_km);
        rideTracker.saveState({ active: true, rideId, startTime: rideStartTime, distance: res.data.distance_km });
      } catch (_e) {}
    }, 10000);

    return () => {
      rideTracker.stopTracking();
      document.removeEventListener('visibilitychange', handleVis);
      if (batchTimerRef.current) { clearInterval(batchTimerRef.current); batchTimerRef.current = null; }
      if (pendingPointsRef.current.length > 0) {
        const batch = [...pendingPointsRef.current];
        pendingPointsRef.current = [];
        axios.post(`${API_URL}/rides/update`, { points: batch }).catch(() => {});
      }
    };
  }, [rideActive, ridePaused, user, rideId, rideStartTime]);

  // ---------- Actions ----------

  const startRide = async () => {
    if (!user) { toast.error(t('rides.loginToTrack')); return; }
    setPendingStartIntro(true);
    try {
      await rideTracker.clearPoints().catch(() => {});
      const res = await axios.post(`${API_URL}/rides/start`, {});
      const startTime = new Date().toISOString();
      setRideActive(true);
      setRidePaused(false);
      setRideId(res.data.id);
      setRideRoute([]);
      setRideDistance(0);
      setRideStartTime(startTime);
      setRideMediaAddedCount(0);
      setMediaJustAdded(false);
      setRideMediaList([]);
      setActiveStop(null);
      setCompletedRideId(null);
      rideTracker.saveState({ active: true, rideId: res.data.id, startTime, distance: 0 });
      toast.success(t('toasts.rideStarted'));
    } catch (err) {
      // Loud, actionable error — the old "Kunne ikke starte tur" was useless.
      console.error('Start ride failed:', err);
      const status = err?.response?.status;
      const serverDetail = err?.response?.data?.detail || err?.response?.data?.message;
      const bodyText = err?.response?.data ? JSON.stringify(err.response.data).slice(0, 200) : null;
      const isNetwork = !err?.response && (err?.code === 'ERR_NETWORK' || err?.message === 'Network Error' || err?.code === 'ECONNABORTED');
      const hasToken = (() => {
        try { return !!localStorage.getItem('token'); } catch { return false; }
      })();
      let msg;
      if (isNetwork) {
        msg = `Netværksfejl — tjek internet. (${err?.code || 'ERR_NETWORK'})`;
      } else if (status === 401 || status === 403) {
        msg = `Ikke logget ind (${status}). Log ud og ind igen.${!hasToken ? ' Ingen token fundet.' : ''}`;
      } else if (status === 429) {
        msg = 'For mange forsøg — vent 1 min.';
      } else if (status >= 500) {
        msg = `Serverfejl ${status}. ${serverDetail || bodyText || 'Prøv igen.'}`;
      } else if (serverDetail) {
        msg = serverDetail;
      } else if (status) {
        msg = `Fejl ${status}${bodyText ? ' · ' + bodyText : ''}`;
      } else {
        msg = err?.message || 'Ukendt fejl';
      }
      const diag = JSON.stringify({
        status, code: err?.code, msg: err?.message,
        has_token: hasToken, url: `${API_URL}/rides/start`,
        response: bodyText,
      });
      toast.error(t('rides.cannotStartRide').replace('{msg}', msg), {
        duration: 12000,
        description: status ? `HTTP ${status}` : (err?.code || t('common.noResponse')),
        action: {
          label: t('common.copyError'),
          onClick: async () => {
            try { await navigator.clipboard.writeText(diag); toast.success(t('rides.errorCopied')); }
            catch { alert(diag); }
          },
        },
      });
    } finally {
      setPendingStartIntro(false);
    }
  };

  const endRide = async () => {
    const endedId = rideId;
    const clearRideState = () => {
      rideTracker.clearState();
      rideTracker.clearPoints();
      rideTracker.stopTracking();
      setRideActive(false);
      setRidePaused(false);
      setActiveStop(null);
    };
    try {
      if (pendingPointsRef.current.length > 0) {
        const batch = [...pendingPointsRef.current];
        pendingPointsRef.current = [];
        try { await axios.post(`${API_URL}/rides/update`, { points: batch }); } catch (_e) {}
      }
      const res = await axios.post(`${API_URL}/rides/end`, {});
      clearRideState();
      toast.success(`${t('rides.rideSaved')} — ${res.data?.distance_km ?? rideDistance} km`);
    } catch {
      clearRideState();
      toast.info(t('rides.rideSaved'));
    }
    // Always open the review screen with whatever ride id we had
    if (endedId) setCompletedRideId(endedId);
  };

  // ---- Pause / Resume: flip local state INSTANTLY for responsive UI,
  //      then fire-and-forget a server-side "manual stop" so media captured
  //      during the pause can be grouped on the backend. ----
  const pauseRide = useCallback(() => {
    setRidePaused(true);
    rideTracker.stopTracking();
    rideTracker.saveState({ active: true, paused: true, rideId, startTime: rideStartTime, distance: rideDistance });
    toast.info(t('rides.ridePaused'));
    if (rideId) {
      (async () => {
        let lat = null, lon = null;
        try {
          const pos = await new Promise((resolve, reject) =>
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true, timeout: 5000,
            })
          );
          lat = pos.coords.latitude; lon = pos.coords.longitude;
        } catch {
          const last = rideRoute[rideRoute.length - 1];
          if (last) { lat = last.latitude; lon = last.longitude; }
        }
        try {
          const res = await axios.post(`${API_URL}/rides/${rideId}/manual-stop`, {
            latitude: lat, longitude: lon,
          });
          setActiveStop(res.data.stop);
        } catch { /* server tracking is best-effort */ }
      })();
    }
  }, [rideId, rideStartTime, rideDistance, rideRoute]);

  const resumeRide = useCallback(() => {
    setRidePaused(false);
    setActiveStop(null);
    rideTracker.saveState({ active: true, paused: false, rideId, startTime: rideStartTime, distance: rideDistance });
    toast.success(t('rides.rideResumed'));
  }, [rideId, rideStartTime, rideDistance]);

  // Backwards-compat aliases used by the old RideControls prop names
  const startManualStop = pauseRide;
  const resumeFromStop = resumeRide;

  // ---- Media upload DURING the ride (direct to server, with GPS) ----
  const uploadRideMedia = useCallback(async (eventOrFile) => {
    const file = eventOrFile?.target?.files?.[0] || eventOrFile;
    if (!file || !rideId) return;

    const isVideo = (file.type || '').startsWith('video');
    if (isVideo && file.size > 200 * 1024 * 1024) {
      toast.error(t('feed.videoTooLarge').replace('{size}', (file.size / 1048576).toFixed(1)));
      if (eventOrFile?.target) eventOrFile.target.value = '';
      return;
    }

    // Current GPS strategy — find the EXACT location where the photo
    // was actually taken, not where the rider is right now.
    //   1. EXIF GPS from the photo file (iPhone & most Android cameras
    //      embed precise lat/lng at the moment the shutter fired).
    //      This is the GROUND TRUTH — works even if the user paused
    //      for 30s before hitting save.
    //   2. Fresh `getCurrentPosition` (cached up to 3s) — fallback
    //      when EXIF has no GPS (videos, edited JPEGs).
    //   3. Last GPS sample from the ride route — last resort when the
    //      browser denies a fresh fix.
    let lat = null, lon = null;
    if (!isVideo) {
      try {
        const { extractPhotoGps } = await import('../utils/photoExifGps');
        const exif = await extractPhotoGps(file);
        if (exif) { lat = exif.latitude; lon = exif.longitude; }
      } catch { /* EXIF read failed — fall through */ }
    }
    if (lat == null || lon == null) {
      try {
        const pos = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true, timeout: 5000, maximumAge: 3000,
          })
        );
        lat = pos.coords.latitude; lon = pos.coords.longitude;
      } catch {
        const last = rideRoute[rideRoute.length - 1];
        if (last) { lat = last.latitude; lon = last.longitude; }
      }
    }

    try {
      const fd = new FormData();
      fd.append('file', file);
      // CRITICAL: videos must go to /posts/upload-video (200MB cap, no
      // image processing). The legacy /posts/upload-image endpoint runs
      // PIL on the bytes and rejects anything >25MB — which is why video
      // recording silently failed before this fix.
      const uploadEndpoint = isVideo ? `${API_URL}/posts/upload-video` : `${API_URL}/posts/upload-image`;
      // Videos can be 100-200MB on slow 4G — give the timeout headroom.
      const uploadTimeout = isVideo ? 240000 : 60000;
      let uploaded = null, uploadErr = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const up = await axios.post(uploadEndpoint, fd, { timeout: uploadTimeout });
          uploaded = up.data; break;
        } catch (err) {
          uploadErr = err;
          // Don't retry on 4xx (file too big, wrong format) — only on
          // network / 5xx errors. Saves time and gives the user the real
          // error message immediately.
          const status = err?.response?.status;
          if (status && status >= 400 && status < 500) break;
          if (attempt < 2) await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
        }
      }
      if (!uploaded) {
        const detail = uploadErr?.response?.data?.detail || uploadErr?.message || 'ukendt fejl';
        throw new Error(detail);
      }

      const saved = await axios.post(`${API_URL}/rides/${rideId}/ride-media`, {
        media_url: uploaded.url,
        media_type: isVideo ? 'video' : 'image',
        latitude: lat,
        longitude: lon,
        stop_id: activeStop?.id || null,
      }, { timeout: 15000 });

      // Push into live list so the user immediately sees the thumbnail
      // on the map and in the controls panel (no "did it actually save?" anxiety).
      const media = saved.data?.media || {
        id: `local_${Date.now()}`,
        url: uploaded.url,
        type: isVideo ? 'video' : 'image',
        latitude: lat,
        longitude: lon,
        timestamp: new Date().toISOString(),
      };
      setRideMediaList(prev => [...prev, media]);
      setRideMediaAddedCount(prev => prev + 1);
      setMediaJustAdded(true);
      setTimeout(() => setMediaJustAdded(false), 1800);
      toast.success(isVideo ? '✅ Video tilføjet' : '✅ Billede tilføjet');
    } catch (err) {
      console.error('Upload failed', err);
      // Show the actual error from the server so the user knows what
      // happened (e.g. "Videoen er 250MB — max 200MB" vs generic).
      const detail = err?.response?.data?.detail || err?.message || 'ukendt fejl';
      toast.error(t('rides.cannotSaveMedia').replace('{detail}', detail));
    }
    if (eventOrFile?.target) eventOrFile.target.value = '';
  }, [rideId, activeStop, rideRoute]);

  // ---- Voice recording via MediaRecorder ----
  const startVoiceRecording = useCallback(async () => {
    if (isVoiceRecording || !rideId) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Prefer webm/opus (Chrome, Firefox, Android). Safari will ignore and pick mp4.
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : (MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '');
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      voiceChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) voiceChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        try { stream.getTracks().forEach(track => track.stop()); } catch (_e) {}
        const blob = new Blob(voiceChunksRef.current, { type: mr.mimeType || 'audio/webm' });
        const duration = Math.max(1, Math.round((Date.now() - voiceStartRef.current) / 1000));
        const ext = (mr.mimeType || '').includes('mp4') ? 'm4a' : 'webm';
        const fd = new FormData();
        fd.append('file', new File([blob], `voice_${Date.now()}.${ext}`, { type: blob.type }));
        fd.append('ride_id', rideId);
        fd.append('duration_sec', String(duration));
        try {
          await axios.post(`${API_URL}/audio/voice/upload`, fd, { timeout: 60000 });
          toast.success(`🎤 Stemme gemt (${duration}s)`);
          setRideMediaAddedCount(prev => prev + 1);
          setMediaJustAdded(true);
          setTimeout(() => setMediaJustAdded(false), 1800);
        } catch (err) {
          toast.error(t('rides.voiceUploadFailed') + ': ' + (err?.response?.data?.detail || err?.message));
        }
      };
      voiceRecorderRef.current = mr;
      voiceStartRef.current = Date.now();
      mr.start();
      setIsVoiceRecording(true);
      setVoiceRecordingSeconds(0);
      voiceTimerRef.current = setInterval(() => {
        setVoiceRecordingSeconds(s => s + 1);
      }, 1000);
    } catch (err) {
      toast.error(t('rides.micDenied') + ': ' + (err?.message || ''));
    }
  }, [isVoiceRecording, rideId]);

  const stopVoiceRecording = useCallback(() => {
    const mr = voiceRecorderRef.current;
    if (mr && mr.state !== 'inactive') { try { mr.stop(); } catch (_e) {} }
    voiceRecorderRef.current = null;
    if (voiceTimerRef.current) { clearInterval(voiceTimerRef.current); voiceTimerRef.current = null; }
    setIsVoiceRecording(false);
    setVoiceRecordingSeconds(0);
  }, []);

  const closeReview = useCallback(() => {
    setCompletedRideId(null);
    setRideRoute([]);
    setRideDistance(0);
    setRideId(null);
    setRideMediaAddedCount(0);
    setRideMediaList([]);
  }, []);

  const value = {
    // state
    rideActive, ridePaused, rideRoute, rideDistance, rideStartTime, rideId,
    pendingStartIntro,
    rideMediaAddedCount, mediaJustAdded,
    rideMediaList,
    activeStop,
    isVoiceRecording, voiceRecordingSeconds,
    completedRideId,
    // actions
    startRide, endRide, pauseRide, resumeRide,
    uploadRideMedia,
    startManualStop, resumeFromStop,
    startVoiceRecording, stopVoiceRecording,
    closeReview,
  };

  // Test/debug hook: allow Playwright (or devtools) to open the post-ride
  // RideReview screen against an existing ride id without going through the
  // GPS-driven start/end flow. Only attaches in non-prod builds and is a
  // no-op otherwise.
  if (typeof window !== 'undefined') {
    window.__rideReviewOpen = (id) => setCompletedRideId(id);
  }

  return <RideContext.Provider value={value}>{children}</RideContext.Provider>;
};
